/**
 * Phase-Driven UI State Selector
 * 
 * Converts raw game state + phase into a UI model for rendering.
 * Ensures all UI logic is aligned with Gutz phases.
 */

/**
 * Get UI state for a specific seat based on game phase and player data
 * @param {Object} params
 * @param {number} seatIndex - Seat index
 * @param {Object} player - Player data
 * @param {string} phase - Current game phase
 * @param {Object} gameState - Full game state
 * @param {string} localPlayerID - Local player ID
 * @returns {Object} UI state for this seat
 */
export function getSeatUIState({ seatIndex, player, phase, gameState, localPlayerID }) {
  const isLocalPlayer = player.playerID === localPlayerID;
  const isPendingJoin = !!(player.pendingJoin || player.inactiveUntilHand);
  const isDealer = !isPendingJoin && gameState.dealerIdx === seatIndex;
  const isActive = !isPendingJoin && Number(gameState.activePlayerIdx) === seatIndex;
  const hasKnocked = !isPendingJoin && (gameState.knocked || []).map(Number).includes(seatIndex);
  const hasDiscarded = !isPendingJoin && (gameState.discarded || []).map(Number).includes(seatIndex);
  const isReady = (gameState.readyPlayers || []).includes(player.playerID);
  const hasVoted = (gameState.suitVotes || []).some(v => v.playerID === player.playerID);
  const isWinner = !isPendingJoin && (Array.isArray(gameState.winnerIdx) 
    ? gameState.winnerIdx.includes(seatIndex)
    : gameState.winnerIdx === seatIndex);
  
  const hand = isPendingJoin ? null : (gameState.hands || [])[seatIndex];
  const credits = (gameState.credits || {})[player.playerID] || 0;
  
  // Determine card visibility based on phase
  let cardVisibility = {
    showCards: false,
    showFaceUp: false,
    showFaceDown: false
  };
  
  if (!isPendingJoin) {
    if (phase === 'deal' || phase === 'action') {
      // During deal/action: local player sees face up, others see face down
      cardVisibility.showCards = !!hand;
      cardVisibility.showFaceUp = isLocalPlayer && !!hand;
      cardVisibility.showFaceDown = !isLocalPlayer && !!hand;
    } else if (phase === 'reveal' || phase === 'results') {
      // During reveal/results: show face up only if they knocked
      cardVisibility.showCards = hasKnocked && !!hand;
      cardVisibility.showFaceUp = hasKnocked && !!hand;
      cardVisibility.showFaceDown = false;
    }
  }
  
  // Determine status badges
  const statusBadges = [];
  if (isPendingJoin) {
    statusBadges.push(player.waitForNormalRound ? 'waiting_normal' : 'joins_next');
  } else {
    if (phase === 'waiting' && isReady) statusBadges.push('ready');
    if (phase === 'suitvote' && hasVoted) statusBadges.push('voted');
    if (phase === 'action' && isActive) statusBadges.push('thinking');
    if (hasKnocked) statusBadges.push('knocked');
    if (hasDiscarded) statusBadges.push('discarded');
    if (isWinner) statusBadges.push('winner');
    if (player.isBot) statusBadges.push('bot');
    if (isDealer) statusBadges.push('dealer');
  }
  
  // Determine visual state
  const visualState = {
    isActive: !isPendingJoin && isActive && phase === 'action',
    isDimmed: isPendingJoin || hasDiscarded,
    isHighlighted: !isPendingJoin && (isWinner || (isActive && phase === 'action')),
    showDealerButton: isDealer && (phase !== 'waiting' && phase !== 'suitvote'),
    showChips: !isPendingJoin && phase === 'deal' && gameState.anteCollected
  };
  
  return {
    seatIndex,
    player,
    isLocalPlayer,
    isPendingJoin,
    isDealer,
    isActive,
    hasKnocked,
    hasDiscarded,
    isReady,
    hasVoted,
    isWinner,
    hand,
    credits,
    cardVisibility,
    statusBadges,
    visualState
  };
}

/**
 * Get global UI state for controls and overlays
 * @param {Object} params
 * @param {string} phase - Current game phase
 * @param {Object} gameState - Full game state
 * @param {string} localPlayerID - Local player ID
 * @param {Array} allSeats - Array of all seats (players + bots) - passed from TableCentric
 * @returns {Object} Global UI state
 */
export function getGlobalUIState({ phase, gameState, localPlayerID, allSeats, localPendingPlayer = null }) {
  // Use allSeats if provided, otherwise try to get from gameState (fallback for compatibility)
  const seats = allSeats || (gameState.players || []).concat(gameState.bots || []);
  const localPlayerIndex = seats.findIndex(p => p.playerID === localPlayerID);
  const localPlayer = seats[localPlayerIndex];

  // Late joiners are visible but have no hand controls until the server promotes them.
  if (localPendingPlayer) {
    const waitNormal = !!localPendingPlayer.waitForNormalRound
      || localPendingPlayer.sitsOutReason === 'special_win_hold';
    return {
      phase,
      controls: {
        showReadyButton: false,
        showSuitVote: false,
        showDealerReady: false,
        showKnockDiscard: false,
        showNextRound: false,
      },
      overlays: {
        showSuitVoteOverlay: false,
        showDealerRevealAnimation: phase === 'dealer_reveal',
        showResultsOverlay: phase === 'results',
        showAllDiscardNotice: gameState.allDiscardNotice,
        showDealerNoFaceCardNotice: gameState.showDealerNoFaceCardNotice,
      },
      pendingJoin: {
        active: true,
        waitForNormalRound: waitNormal,
        message: waitNormal
          ? 'Waiting for next normal round'
          : 'Joins next hand',
      },
      localHandRanking: null,
      pot: gameState.pot || 0,
      creditValue: gameState.creditValue || 1.00,
      round: gameState.round || 1
    };
  }
  
  // Debug logging for action phase
  if (phase === 'action') {
    console.log(`DEBUG: getGlobalUIState - phase: ${phase}, localPlayerID: ${localPlayerID}, localPlayerIndex: ${localPlayerIndex}, activePlayerIdx: ${gameState.activePlayerIdx}, knocked: [${gameState.knocked || []}], discarded: [${gameState.discarded || []}]`);
    console.log(`DEBUG: getGlobalUIState - allSeats:`, seats.map((s, i) => `${i}:${s.playerID}`).join(', '));
  }
  
  // Determine which controls should be enabled
  // Coerce seat indices — string/number mismatches can leave one client on knock/discard.
  const activeIdx = Number(gameState.activePlayerIdx);
  const knocked = (gameState.knocked || []).map(Number);
  const discarded = (gameState.discarded || []).map(Number);
  // Banner/notice flags can appear slightly before phase flips; never keep knock UI in that window.
  const handResolutionPending =
    !!gameState.showDealerChangeNotice ||
    !!gameState.showDealerNoFaceCardNotice ||
    !!gameState.allDiscardNotice;

  const showReadyButton = phase === 'waiting' && !(gameState.readyPlayers || []).includes(localPlayerID);
  if (phase === 'waiting') {
    console.log(`DEBUG: getGlobalUIState - waiting phase. showReadyButton: ${showReadyButton}, localPlayerID: ${localPlayerID}, readyPlayers:`, gameState.readyPlayers || []);
  }
  const controls = {
    showReadyButton: showReadyButton,
    showSuitVote: phase === 'suitvote' && !(gameState.suitVotes || []).some(v => v.playerID === localPlayerID),
    showDealerReady: (phase === 'dealer_reveal' || phase === 'dealer_selected') && 
                     (gameState.dealerRevealFound != null || gameState.dealerIdx != null) &&
                     !(gameState.readies || []).includes(localPlayerID),
    // Server phase is authoritative — leave action as soon as phase moves on.
    showKnockDiscard: phase === 'action' &&
                      !handResolutionPending &&
                      localPlayerIndex >= 0 &&
                      Number.isFinite(activeIdx) &&
                      activeIdx === localPlayerIndex &&
                      !knocked.includes(localPlayerIndex) &&
                      !discarded.includes(localPlayerIndex),
    showNextRound: phase === 'results' && !(gameState.readies || []).includes(localPlayerID)
  };
  
  if (phase === 'action') {
    console.log(`DEBUG: getGlobalUIState - controls.showKnockDiscard: ${controls.showKnockDiscard}`);
  }
  
  // Determine which overlays to show
  const overlays = {
    showSuitVoteOverlay: phase === 'suitvote',
    showDealerRevealAnimation: phase === 'dealer_reveal',
    showResultsOverlay: phase === 'results',
    showAllDiscardNotice: gameState.allDiscardNotice,
    showDealerNoFaceCardNotice: gameState.showDealerNoFaceCardNotice
  };
  
  // Get hand ranking for local player (if revealed)
  let localHandRanking = null;
  if (localPlayerIndex >= 0 && gameState.hands && gameState.hands[localPlayerIndex]) {
    const hand = gameState.hands[localPlayerIndex];
    localHandRanking = getHandRanking(hand);
  }
  
  // Debug: Log if allSeats was provided
  if (phase === 'action' && !allSeats) {
    console.warn(`WARNING: getGlobalUIState called without allSeats parameter!`);
  }
  
  return {
    phase,
    controls,
    overlays,
    pendingJoin: null,
    localHandRanking,
    pot: gameState.pot || 0,
    creditValue: gameState.creditValue || 1.00,
    round: gameState.round || 1
  };
}

/**
 * Get Gutz hand ranking label (for UI display only)
 * @param {Array} hand - Array of 2 cards
 * @returns {string} Hand ranking label
 */
function getHandRanking(hand) {
  if (!hand || hand.length !== 2) return null;
  
  const [card1, card2] = hand;
  const rank1 = card1.rank;
  const rank2 = card2.rank;
  
  // Check for 6/9 special
  if ((rank1 === '6' && rank2 === '9') || (rank1 === '9' && rank2 === '6')) {
    return '6/9 Special';
  }
  
  // Check for pair
  if (rank1 === rank2) {
    return `Pair of ${rank1}`;
  }
  
  // High card
  const ranks = ['A', 'K', 'Q', 'J', '10', '9', '8', '7', '6', '5', '4', '3', '2'];
  const highRank = ranks.indexOf(rank1) < ranks.indexOf(rank2) ? rank1 : rank2;
  return `High Card ${highRank}`;
}

